/**
 * Customer-facing receipt page (V0080).
 *
 * Reached via the SMS short link
 * (``notifications.<env>.apps.myfinterra.com/r/<slug>``). Public - no
 * auth, no AppShell chrome. Slug is the access check on the server;
 * a missing / tampered slug returns 404 from
 * ``GET /pos/v1/public/receipts/by-slug/{slug}`` and we render a
 * friendly empty state.
 *
 * Renders three templates keyed off the envelope's ``kind`` field:
 *   * receipt_order  - full charge receipt for the order (totals + payments)
 *   * receipt_refund - refund-specific confirmation
 *   * receipt_void   - void confirmation
 */

import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { fetchPublicReceiptBySlug } from "../lib/api";
import { formatCents, formatDateTimeWithYear } from "../lib/format";

interface OrderShape {
  readonly id: string;
  readonly currency?: string;
  readonly total_cents?: number;
  readonly paid_cents?: number;
  readonly refunded_cents?: number;
  readonly discount_cents?: number;
  readonly discount_label?: string | null;
  readonly discounts?: ReadonlyArray<{
    readonly id: string;
    readonly discount_type_display_name: string;
    readonly amount_cents: number;
    readonly percent_bps: number | null;
  }> | null;
  readonly status?: string;
  readonly customer_name?: string | null;
  readonly description?: string | null;
  readonly external_reference?: string | null;
  readonly created_at?: string;
  readonly partner_brand?: {
    readonly name?: string;
    readonly logo_url?: string;
    readonly tagline?: string | null;
    readonly powered_by?: string | null;
    readonly organization_name?: string | null;
    readonly location_name?: string | null;
  } | null;
  /** V0029 IANA timezone of the order's location. When set, all
   *  timestamps on the receipt render in this timezone instead of
   *  the customer's device default - matches the in-app receipt
   *  copy (cashier-typed time = customer-displayed time). */
  readonly location_timezone?: string | null;
}

interface PaymentShape {
  readonly payment_id: string;
  readonly role: string;
  readonly applied_amount_cents: number;
  readonly payment_type?: string;
  readonly payment_subtype?: string | null;
  readonly status?: string;
  readonly external_ref?: string | null;
  readonly created_at?: string;
  readonly pan_last4?: string | null;
  readonly auth_code?: string | null;
  readonly card_brand?: string | null;
}

export function PublicReceiptPage() {
  const params = useParams();
  const slug = params.slug ?? "";

  const query = useQuery({
    queryKey: ["public-receipt", slug],
    queryFn: () => fetchPublicReceiptBySlug(slug),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(slug),
  });

  if (query.isLoading) {
    return (
      <PageShell>
        <p className="text-sm text-muted">Loading receipt…</p>
      </PageShell>
    );
  }
  if (query.isError) {
    return (
      <PageShell>
        <h1 className="text-xl font-semibold text-strong">Couldn't load this receipt</h1>
        <p className="mt-2 text-sm text-muted">
          Please check the link or try again later. If the issue persists, contact the merchant who
          sent you the receipt.
        </p>
      </PageShell>
    );
  }
  if (!query.data) {
    return (
      <PageShell>
        <h1 className="text-xl font-semibold text-strong">This receipt link is no longer valid</h1>
        <p className="mt-2 text-sm text-muted">
          Receipt links are issued per transaction. If the merchant re-sent you a newer link, please
          use that one instead.
        </p>
      </PageShell>
    );
  }

  const { kind, order, payments, featured_payment_id, vendor_receipt_url } = query.data;
  const o = order as unknown as OrderShape;
  const p = payments as unknown as ReadonlyArray<PaymentShape>;

  // Processor receipt (e.g. Valor eReceipt) takes precedence when available.
  // Falls back to our React template for all kinds. Void receipts never have
  // a processor artifact.
  if (vendor_receipt_url && kind !== "receipt_void") {
    return <VendorReceipt url={vendor_receipt_url as string} />;
  }

  if (kind === "receipt_payment") {
    return (
      <PaymentReceipt order={o} payments={p} featuredPaymentId={featured_payment_id as string} />
    );
  }
  if (kind === "receipt_refund") {
    return <RefundReceipt order={o} payments={p} />;
  }
  if (kind === "receipt_void") {
    return <VoidReceipt order={o} payments={p} />;
  }
  return <OrderReceipt order={o} payments={p} />;
}

/** Resolve the timezone the receipt should render its timestamps in.
 *  Prefer the location's IANA tz so the customer sees the merchant
 *  time (matches the in-app receipt + the SMS body's "at HH:MM"
 *  copy). Falls back to undefined → browser default when the public
 *  envelope didn't carry one (older tenants / column missing). */
function tzFor(order: OrderShape): string | undefined {
  return order.location_timezone ?? undefined;
}

function VendorReceipt({ url }: { readonly url: string }) {
  return (
    <iframe
      src={url}
      title="Receipt"
      sandbox="allow-scripts allow-forms"
      className="fixed inset-0 h-full w-full border-0"
    />
  );
}

function PageShell(props: { readonly children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-canvas px-4 py-8 text-foreground">
      <div className="mx-auto w-full max-w-md rounded-lg border border-line bg-surface p-6 shadow-elev-1">
        {props.children}
      </div>
    </div>
  );
}

function OrderReceipt({
  order,
  payments,
}: {
  readonly order: OrderShape;
  readonly payments: ReadonlyArray<PaymentShape>;
}) {
  const currency = order.currency ?? "USD";
  const charges = payments.filter((p) => p.role === "charge");
  const refunds = payments.filter((p) => p.role === "refund");
  return (
    <PageShell>
      <ReceiptHeader brand={order.partner_brand} title="Receipt" />
      <ReceiptMeta order={order} />

      <div className="mt-6 rounded-md bg-surface-muted px-4 py-3">
        {Number(order.discount_cents ?? 0) > 0 ? (
          <>
            <Row label="Subtotal" value={formatCents(Number(order.total_cents ?? 0), currency)} />
            {(order.discounts ?? []).map((d) => (
              <Row
                key={d.id}
                label={
                  d.percent_bps != null
                    ? `${d.discount_type_display_name} (${(d.percent_bps / 100).toFixed(d.percent_bps % 100 === 0 ? 0 : 2)}%)`
                    : d.discount_type_display_name
                }
                value={`-${formatCents(d.amount_cents, currency)}`}
              />
            ))}
            {(order.discounts ?? []).length === 0 && (
              <Row
                label={order.discount_label ?? "Discount"}
                value={`-${formatCents(Number(order.discount_cents ?? 0), currency)}`}
              />
            )}
            <Row
              label="Total"
              value={formatCents(
                Math.max(0, Number(order.total_cents ?? 0) - Number(order.discount_cents ?? 0)),
                currency,
              )}
              bold
            />
          </>
        ) : (
          <Row label="Total" value={formatCents(Number(order.total_cents ?? 0), currency)} bold />
        )}
        <Row label="Paid" value={formatCents(Number(order.paid_cents ?? 0), currency)} />
        {Number(order.refunded_cents ?? 0) > 0 && (
          <Row
            label="Refunded"
            value={`-${formatCents(Number(order.refunded_cents ?? 0), currency)}`}
          />
        )}
      </div>

      <h2 className="mt-6 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
        Payments
      </h2>
      <ul className="mt-2 divide-y divide-line/60 rounded-md ring-1 ring-line/60">
        {charges.map((c) => (
          <PaymentLine key={c.payment_id} payment={c} currency={currency} tz={tzFor(order)} />
        ))}
      </ul>

      {refunds.length > 0 && (
        <>
          <h2 className="mt-6 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
            Refunds
          </h2>
          <ul className="mt-2 divide-y divide-line/60 rounded-md ring-1 ring-line/60">
            {refunds.map((r) => (
              <PaymentLine
                key={r.payment_id}
                payment={r}
                currency={currency}
                negative
                tz={tzFor(order)}
              />
            ))}
          </ul>
        </>
      )}

      <ReceiptFooter brand={order.partner_brand} />
    </PageShell>
  );
}

function PaymentReceipt({
  order,
  payments,
  featuredPaymentId,
}: {
  readonly order: OrderShape;
  readonly payments: ReadonlyArray<PaymentShape>;
  readonly featuredPaymentId: string;
}) {
  const currency = order.currency ?? "USD";
  const payment = payments.find((p) => p.payment_id === featuredPaymentId);
  const amount = payment
    ? Number(payment.applied_amount_cents ?? 0)
    : Number(order.paid_cents ?? 0);
  return (
    <PageShell>
      <ReceiptHeader brand={order.partner_brand} title="Receipt" />
      <ReceiptMeta order={order} />

      <div className="mt-6 rounded-md bg-surface-muted px-4 py-3">
        <Row label="Amount" value={formatCents(amount, currency)} bold />
      </div>

      {payment && (
        <>
          <h2 className="mt-6 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
            Payment
          </h2>
          <ul className="mt-2 divide-y divide-line/60 rounded-md ring-1 ring-line/60">
            <PaymentLine payment={payment} currency={currency} tz={tzFor(order)} />
          </ul>
        </>
      )}

      <ReceiptFooter brand={order.partner_brand} />
    </PageShell>
  );
}

function RefundReceipt({
  order,
  payments,
}: {
  readonly order: OrderShape;
  readonly payments: ReadonlyArray<PaymentShape>;
}) {
  const currency = order.currency ?? "USD";
  // Prefer the most recent refund row to render the "this refund"
  // amount; the URL pins one specific refund payment via target_id
  // but we don't surface it here - the public envelope already
  // returned its row in the payments list.
  const refunds = payments.filter((p) => p.role === "refund");
  const refund = refunds[refunds.length - 1];
  return (
    <PageShell>
      <ReceiptHeader brand={order.partner_brand} title="Refund confirmation" />
      <ReceiptMeta order={order} />

      <div className="mt-6 rounded-md bg-surface-muted px-4 py-3">
        <Row
          label="Refunded"
          value={
            refund
              ? `-${formatCents(Number(refund.applied_amount_cents ?? 0), currency)}`
              : `-${formatCents(Number(order.refunded_cents ?? 0), currency)}`
          }
          bold
        />
        {refund?.created_at && (
          <Row label="When" value={formatDateTimeWithYear(refund.created_at, tzFor(order))} />
        )}
      </div>

      <ReceiptFooter brand={order.partner_brand} />
    </PageShell>
  );
}

function VoidReceipt({
  order,
  payments,
}: {
  readonly order: OrderShape;
  readonly payments: ReadonlyArray<PaymentShape>;
}) {
  const currency = order.currency ?? "USD";
  // Void receipts list every charge that was retracted so the customer
  // sees what's been pulled back. Today no payments survive a void
  // (the API blocks voiding orders with paid_cents > 0) but we render
  // any historical charges for completeness.
  const charges = payments.filter((p) => p.role === "charge");
  return (
    <PageShell>
      <ReceiptHeader brand={order.partner_brand} title="Order voided" />
      <ReceiptMeta order={order} />
      <div className="mt-6 rounded-md bg-surface-muted px-4 py-3">
        <Row
          label="Order total (voided)"
          value={formatCents(Number(order.total_cents ?? 0), currency)}
          bold
        />
      </div>
      {charges.length > 0 && (
        <>
          <h2 className="mt-6 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
            Voided charges
          </h2>
          <ul className="mt-2 divide-y divide-line/60 rounded-md ring-1 ring-line/60">
            {charges.map((c) => (
              <PaymentLine
                key={c.payment_id}
                payment={c}
                currency={currency}
                struck
                tz={tzFor(order)}
              />
            ))}
          </ul>
        </>
      )}
      <ReceiptFooter brand={order.partner_brand} />
    </PageShell>
  );
}

function ReceiptHeader({
  brand,
  title,
}: {
  readonly brand: OrderShape["partner_brand"];
  readonly title: string;
}) {
  return (
    <header className="flex flex-col items-center gap-2 border-b border-line pb-4 text-center">
      {brand?.logo_url && (
        <img
          src={brand.logo_url}
          alt={brand.name ?? "Merchant logo"}
          className="h-10 max-w-[160px] object-contain"
        />
      )}
      {brand?.name && <p className="text-sm font-semibold text-strong">{brand.name}</p>}
      {brand?.organization_name && brand.organization_name !== brand.name && (
        <p className="text-sm text-foreground">{brand.organization_name}</p>
      )}
      {brand?.location_name && <p className="text-xs text-muted">{brand.location_name}</p>}
      <h1 className="text-2xl font-bold tracking-tight text-strong">{title}</h1>
    </header>
  );
}

function ReceiptMeta({ order }: { readonly order: OrderShape }) {
  return (
    <dl className="mt-4 space-y-1 text-sm">
      {order.external_reference && <Row label="Reference" value={order.external_reference} />}
      <Row label="Order" value={`#${order.id.split("-")[0]?.toUpperCase() ?? order.id}`} />
      {order.customer_name && <Row label="Customer" value={order.customer_name} />}
      {order.description && <Row label="For" value={order.description} />}
      {order.created_at && (
        <Row label="Date" value={formatDateTimeWithYear(order.created_at, tzFor(order))} />
      )}
    </dl>
  );
}

function ReceiptFooter({ brand }: { readonly brand: OrderShape["partner_brand"] }) {
  // Footer copy resolution order:
  //   1. brand.powered_by - explicit override the partner set in
  //      brand_config (e.g. "Receipts powered by Honda" for a
  //      white-labelled tenant).
  //   2. "Receipt provided by <brand.name>" - derived fallback so a
  //      tenant who only set product_name still gets a sane footer
  //      pointing at their own brand instead of the platform's.
  //   3. Generic "questions? contact the merchant" - when no brand
  //      info came through at all (older tenants pre-V0080 cascade).
  // Critically: no hard-coded platform name. White-labelled tenants
  // never see "Powered by Tilt" leak onto their customer receipts.
  const explicit = brand?.powered_by?.trim();
  const merchant = brand?.name?.trim();
  const copy =
    explicit ||
    (merchant && `Receipt provided by ${merchant} - questions? Contact them directly.`) ||
    "Questions about this receipt? Contact the merchant directly.";
  return (
    <p className="mt-6 border-t border-line pt-4 text-center text-[0.6875rem] text-muted">{copy}</p>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  readonly label: string;
  readonly value: string;
  readonly bold?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd
        className={bold ? "font-semibold tabular-nums text-strong" : "tabular-nums text-foreground"}
      >
        {value}
      </dd>
    </div>
  );
}

function PaymentLine({
  payment,
  currency,
  negative,
  struck,
  tz,
}: {
  readonly payment: PaymentShape;
  readonly currency: string;
  readonly negative?: boolean;
  readonly struck?: boolean;
  readonly tz?: string | undefined;
}) {
  const method = (payment.payment_subtype ?? payment.payment_type ?? "").replace(/_/g, " ");
  const cardLabel = [payment.card_brand, payment.pan_last4 ? `••••${payment.pan_last4}` : null]
    .filter(Boolean)
    .join(" ");
  const cls = [
    "text-sm font-semibold tabular-nums",
    negative ? "text-red-600" : "text-strong",
    struck ? "line-through text-muted" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
      <span className="flex flex-col">
        <span className="font-medium text-foreground capitalize">{method || "Payment"}</span>
        {cardLabel && <span className="text-[0.6875rem] text-faint">{cardLabel}</span>}
        {payment.auth_code && (
          <span className="text-[0.6875rem] text-faint">Auth: {payment.auth_code}</span>
        )}
        {payment.created_at && (
          <span className="text-[0.6875rem] tabular-nums text-faint">
            {formatDateTimeWithYear(payment.created_at, tz)}
          </span>
        )}
      </span>
      <span className={cls}>
        {negative ? "−" : ""}
        {formatCents(Number(payment.applied_amount_cents ?? 0), currency)}
      </span>
    </li>
  );
}
